import { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useSearchParams, Navigate } from "react-router-dom";
import Health from "@dashboard/components/health/Health";
import Layout from "@dashboard/components/main-layout/main-layout";
import "@domun/component-library/lib/styles/icomoon-font.prod.css";
import "@domun/component-library/lib/styles/icons.css";
import CartsList from "./modules/abandoned-cart/pages/carts-list/carts-list";
import { useGlobalData } from "./hooks/use-global-data.hook";
import Home from "./modules/home/pages/home";
import Personalization from "./modules/personalization/pages/personalization";
import ContactButtonAddon from "./modules/contact-button/pages/contact-button-addon";
import { PROFILE_ROUTES } from "./modules/profile/profile.routes";
import LoginQrPage from "@dashboard/components/login-qr-page/login-qr-page";
import { ORDERS_ROUTES } from "./modules/orders/orders.routes";
import { DELIVERY_ROUTES } from "./modules/delivery/delivery.routes";
import { CUSTOMERS_ROUTES } from "./modules/customers/customers.routes";
import Login from "./modules/login/login";
import LoaderStatus from "./components/loader-status/loader-status";
import MeliValidator from "./modules/meli/components/meli-validator/meli-validator";
import Guest from "./components/guest/guest";
import ConfigurationLayout from "./modules/configuration/layout";
import PaymentGatewayLayout from "./modules/configuration/pages/payment-gateway/layout/payment-gateway.layout";
import { useDeeplinks } from "./hooks/deeplinks.hook";
import Mobile from "./modules/mobile/pages/mobile/mobile";
import OnboardingInfo from "./modules/onboarding-general/pages/onboarding-info/onboarding-info";
import OnboardingStore from "./modules/onboarding-general/pages/onboarding-store/onboarding-store";
import OnboardingDemo from "./modules/onboarding-general/pages/onboarding-demo/onboarding-demo";
import OnboardingGenerating from "./modules/onboarding-general/pages/onboarding-generating/onboarding-generating";
import OnboardingCustomization from "./modules/onboarding-general/pages/onboarding-customization/onboarding-customization";
import OnboardingPlan from "./modules/onboarding-general/pages/onboarding-plan/onboarding-plan";
import OnboardingGuestGenerating from "./modules/onboarding-guest/pages/onboarding-guest-generating/onboarding-guest-generating";
import OnboardingGuestPersonalize from "./modules/onboarding-guest/pages/onboarding-guest-personalize/onboarding-guest-personalize";
import OnboardingGuestInfo from "./modules/onboarding-guest/pages/onboarding-guest-info/onboarding-guest-info";
import OnboardingGuestPlan from "./modules/onboarding-guest/pages/onboarding-guest-plan/onboarding-guest-plan";
import OnboardingGuest from "./modules/onboarding-guest/onboarding-guest";
import OnboardingGeneral from "./modules/onboarding-general/onboarding-general";
import ProductForm from "@dashboard/modules/products/components/product-component/layout/product-form";
import ProductFormIndex from "@dashboard/modules/products/components/product-component/layout/product-form-index";
import ProductTypeRouteGuard from "@dashboard/modules/products/components/product-component/layout/product-type-route-guard";
import { GOOGLE_SHOPPING_ROUTES } from "./modules/google-shopping/google-shopping.routes";
import { useClevertap } from "./hooks/clevertap.hook";
import { CATEGORIES_ROUTES } from "./modules/categories/constants/categories.constants";
import { MARKETPLACE_ROUTES } from "./modules/marketplace/constants/marketplace.constants";
import DeliveryLayout from "@dashboard/modules/delivery";
import DeliveryHome from "@dashboard/modules/delivery/pages/delivery-home/delivery-home";
import DeliverySettings from "@dashboard/modules/delivery/pages/delivery-settings/delivery-settings";
import DeliveryCoverage from "@dashboard/modules/delivery/pages/delivery-coverage/delivery-coverage";
import DeliveryCreate from "@dashboard/modules/delivery/pages/delivery-create/delivery-create";
import DeliveryCheckout from "@dashboard/modules/delivery/pages/delivery-checkout/delivery-checkout";
import DeliveryDetail from "@dashboard/modules/delivery/pages/delivery-detail/delivery-detail";

function App() {
  const ProductListView = lazy(
    () => import("@dashboard/modules/products/views/list-view")
  );
  const BulkUploadProducts = lazy(
    () => import("@dashboard/modules/products/views/bulk-upload-products")
  );
  const BulkUploadFile = lazy(
    () => import("@dashboard/modules/products/views/bulk-upload-file")
  );
  const BulkUploadImages = lazy(
    () => import("@dashboard/modules/products/views/bulk-upload-images")
  );
  const BulkProductsUploadImages = lazy(
    () =>
      import("@dashboard/modules/products/views/bulk-products-upload-images")
  );
  const BulkProductsCreate = lazy(
    () => import("@dashboard/modules/products/views/bulk-products-create")
  );
  const BulkProductsRetry = lazy(
    () => import("@dashboard/modules/products/views/bulk-products-retry")
  );
  const BulkUploadCustomFile = lazy(
    () => import("@dashboard/modules/products/views/bulk-upload-custom-file")
  );
  const InventoryView = lazy(
    () => import("@dashboard/modules/products/views/inventory-view")
  );
  const Plans = lazy(() => import("@dashboard/modules/plans/plans"));
  const Profile = lazy(() => import("@dashboard/modules/profile/profile"));
  const ProfileHome = lazy(
    () => import("@dashboard/modules/profile/views/home")
  );
  const BusinessSectors = lazy(
    () => import("@dashboard/modules/profile/views/business-sectors")
  );
  const StoreHours = lazy(
    () => import("@dashboard/modules/profile/views/store-hours")
  );

  const Orders = lazy(() => import("@dashboard/modules/orders/orders"));
  const OrderDetail = lazy(
    () => import("@dashboard/modules/orders/views/deatil")
  );
  const OrderCreate = lazy(
    () => import("@dashboard/modules/orders/views/create")
  );
  const Checkout = lazy(
    () => import("@dashboard/modules/checkout/pages/checkout")
  );
  const Status = lazy(() => import("@dashboard/modules/checkout/pages/status"));
  const VoucherList = lazy(
    () => import("@dashboard/modules/voucher/pages/voucher-list")
  );
  const VoucherCreation = lazy(
    () => import("@dashboard/modules/voucher/pages/voucher-creation")
  );
  const OffersList = lazy(
    () => import("@dashboard/modules/offers/pages/offers-list")
  );
  const OfferWizard = lazy(
    () => import("@dashboard/modules/offers/pages/offer-wizard")
  );
  const OfferDetail = lazy(
    () => import("@dashboard/modules/offers/pages/offer-detail")
  );
  const CouponsList = lazy(
    () => import("@dashboard/modules/coupons/pages/coupons-list")
  );
  const CouponWizard = lazy(
    () => import("@dashboard/modules/coupons/pages/coupon-wizard")
  );
  const CouponDetail = lazy(
    () => import("@dashboard/modules/coupons/pages/coupon-detail")
  );
  const KitsList = lazy(
    () => import("@dashboard/modules/kits/pages/kits-list")
  );
  const KitProductSelector = lazy(
    () => import("@dashboard/modules/kits/pages/kit-product-selector")
  );
  const KitForm = lazy(() => import("@dashboard/modules/kits/pages/kit-form"));
  const MeliModule = lazy(
    () => import("@dashboard/modules/meli/views/meli/meli")
  );
  const GoogleShoppingModule = lazy(
    () => import("@dashboard/modules/google-shopping/google-shopping")
  );

  const GoogleShoppingInfoModule = lazy(
    () =>
      import("@dashboard/modules/google-shopping/views/google-shopping-info")
  );
  const GoogleShoppingConfigurationModule = lazy(
    () =>
      import(
        "@dashboard/modules/google-shopping/views/google-shopping-configuration/google-shopping-configuration"
      )
  );
  const Configuration = lazy(
    () => import("@dashboard/modules/configuration/pages/configuration")
  );
  const Domain = lazy(
    () => import("@dashboard/modules/configuration/pages/domain")
  );
  const TermsAndConditionsPage = lazy(
    () =>
      import(
        "@dashboard/modules/terms-and-conditions/pages/terms-and-conditions"
      )
  );
  const SearchDomain = lazy(
    () => import("@dashboard/modules/configuration/pages/search-domain")
  );
  const PaymentGatewayModule = lazy(
    () =>
      import(
        "@dashboard/modules/configuration/pages/payment-gateway/payment-gateway"
      )
  );
  const PaymentGatewayDetailModule = lazy(
    () =>
      import(
        "@dashboard/modules/configuration/pages/payment-gateway/payment-gateway-detail/payment-gateway-detail"
      )
  );
  const ManualPaymentModule = lazy(
    () =>
      import(
        "@dashboard/modules/configuration/pages/payment-gateway/manual-payment/manual-payment"
      )
  );

  const ProductImageModule = lazy(
    () =>
      import(
        "@dashboard/modules/products/components/product-component/views/product-image/product-image"
      )
  );

  const ProductFeatureListModule = lazy(
    () =>
      import(
        "@dashboard/modules/products/components/product-component/views/product-feature-list/product-feature-list"
      )
  );

  const ProductFeatureFormModule = lazy(
    () =>
      import(
        "@dashboard/modules/products/components/product-component/views/product-feature-form/product-feature-form"
      )
  );

  const ServiceProfessionalsModule = lazy(
    () =>
      import(
        "@dashboard/modules/products/components/product-component/views/service-professionals/service-professionals"
      )
  );

  const OnboardingAiModule = lazy(
    () => import("@dashboard/modules/onboarding-ai/index")
  );

  const CategoriesModule = lazy(
    () => import("@dashboard/modules/categories/index")
  );
  const CategoryListModule = lazy(
    () => import("@dashboard/modules/categories/views/category-list-view/index")
  );
  const CategoryFormModule = lazy(
    () => import("@dashboard/modules/categories/views/category-form-view/index")
  );
  const CategoryViewModule = lazy(
    () => import("@dashboard/modules/categories/views/category-view/index")
  );
  const CategoryProductsViewModule = lazy(
    () =>
      import("@dashboard/modules/categories/views/category-products-view/index")
  );

  const MarketplaceModule = lazy(
    () => import("@dashboard/modules/marketplace/index")
  );
  const MarketplaceView = lazy(
    () => import("@dashboard/modules/marketplace/pages/marketplace-view/index")
  );
  const AddonDetailView = lazy(
    () => import("@dashboard/modules/marketplace/pages/addon-detail-view/index")
  );
  const AddonWebviewView = lazy(
    () =>
      import("@dashboard/modules/marketplace/pages/addon-webview-view/index")
  );
  const PartnershipDetailView = lazy(
    () =>
      import(
        "@dashboard/modules/marketplace/pages/partnership-detail-view/index"
      )
  );

  const SeoOverview = lazy(
    () => import("@dashboard/modules/seo/pages/seo-overview")
  );
  const SeoProductDetail = lazy(
    () => import("@dashboard/modules/seo/pages/seo-product-detail")
  );

  const GaConfig = lazy(
    () => import("@dashboard/modules/google-analytics/pages/ga-config")
  );
  const GaFindId = lazy(
    () => import("@dashboard/modules/google-analytics/pages/ga-find-id")
  );
  const CatalogSorterPage = lazy(
    () => import("@dashboard/modules/catalog-sorter/pages/catalog-sorter-page")
  );
  const CustomersList = lazy(
    () => import("@dashboard/modules/customers/pages/customers-list")
  );
  const CustomersGroup = lazy(
    () => import("@dashboard/modules/customers/pages/customers-group")
  );
  const CustomerDetail = lazy(
    () => import("@dashboard/modules/customers/pages/customer-detail")
  );
  const FbPixelHome = lazy(
    () => import("@dashboard/modules/facebook-pixel/pages/fb-pixel-home")
  );
  const FbPixelConnect = lazy(
    () => import("@dashboard/modules/facebook-pixel/pages/fb-pixel-connect")
  );
  const FbPixelHelp = lazy(
    () => import("@dashboard/modules/facebook-pixel/pages/fb-pixel-help")
  );

  const [searchParams] = useSearchParams();
  const { valideDeeplink } = useDeeplinks();
  const { init: initClevertap } = useClevertap();
  const token = searchParams.get("t");

  useGlobalData();

  useEffect(() => {
    if (token) {
      localStorage.setItem("accessToken", `"${token}"`);
    }
  }, [token]);

  useEffect(() => {
    initClevertap();
    valideDeeplink();
  }, []);

  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/status" element={<LoaderStatus />} />
      <Route
        path="/onboarding-ai"
        element={
          <Suspense>
            <OnboardingAiModule />
          </Suspense>
        }
      />
      <Route path="/guest" element={<Guest />} />
      <Route path="/activate" element={<LoginQrPage />} />
      <Route path="/onboarding" element={<OnboardingGeneral />}>
        <Route path="info" element={<OnboardingInfo />} />
        <Route path="store" element={<OnboardingStore />} />
        <Route path="templates" element={<OnboardingDemo />} />
        <Route path="generating" element={<OnboardingGenerating />} />
        <Route path="personalize" element={<OnboardingCustomization />} />
        <Route path="premium" element={<OnboardingPlan />} />
      </Route>
      <Route path="/onboarding-split-two" element={<OnboardingGuest />}>
        {" "}
        {/* TODO: remover fallback ruta antigua cuando se migren las deps */}
        <Route path="generating" element={<OnboardingGuestGenerating />} />
      </Route>
      <Route path="/onboarding-guest" element={<OnboardingGuest />}>
        <Route path="personalize" element={<OnboardingGuestPersonalize />} />
        <Route path="info" element={<OnboardingGuestInfo />} />
        <Route path="premium" element={<OnboardingGuestPlan />} />
        <Route path="generating" element={<OnboardingGuestGenerating />} />
      </Route>
      <Route path="/integrations">
        <Route path="meli-validator" element={<MeliValidator />} />
      </Route>
      <Route path="/:id" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="products">
          <Route
            index
            element={
              <Suspense>
                <ProductListView />
              </Suspense>
            }
          />
          <Route
            path="inventory"
            element={
              <Suspense>
                <InventoryView />
              </Suspense>
            }
          />
          <Route path="edit" element={<Navigate to="new" replace />} />
          <Route path="edit/:productId" element={<ProductForm />}>
            <Route index element={<ProductFormIndex />} />
            <Route
              path="images"
              element={
                <Suspense>
                  <ProductImageModule />
                </Suspense>
              }
            />
            <Route
              path="stock"
              element={
                <ProductTypeRouteGuard>
                  <Suspense>
                    <ProductFeatureListModule />
                  </Suspense>
                </ProductTypeRouteGuard>
              }
            />
            <Route
              path="features"
              element={
                <ProductTypeRouteGuard>
                  <Suspense>
                    <ProductFeatureFormModule />
                  </Suspense>
                </ProductTypeRouteGuard>
              }
            />
            <Route
              path="professionals"
              element={
                <ProductTypeRouteGuard serviceOnly>
                  <Suspense>
                    <ServiceProfessionalsModule />
                  </Suspense>
                </ProductTypeRouteGuard>
              }
            />
          </Route>
          <Route
            path="bulk-upload"
            element={
              <Suspense>
                <BulkUploadProducts />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <Suspense>
                  <BulkUploadFile />
                </Suspense>
              }
            />
            <Route
              path="images"
              element={
                <Suspense>
                  <BulkUploadImages />
                </Suspense>
              }
            />
            <Route
              path="images/bulk-upload"
              element={
                <Suspense>
                  <BulkProductsUploadImages />
                </Suspense>
              }
            />
            <Route
              path="create"
              element={
                <Suspense>
                  <BulkProductsCreate />
                </Suspense>
              }
            />
            <Route
              path="summary"
              element={
                <Suspense>
                  <BulkProductsRetry />
                </Suspense>
              }
            />
            <Route
              path="custom-file"
              element={
                <Suspense>
                  <BulkUploadCustomFile />
                </Suspense>
              }
            />
          </Route>
        </Route>
        <Route
          path={CATEGORIES_ROUTES.ROOT}
          element={<Navigate to={CATEGORIES_ROUTES.LIST} replace />}
        />
        <Route
          path={CATEGORIES_ROUTES.ROOT}
          element={
            <Suspense>
              <CategoriesModule />
            </Suspense>
          }
        >
          <Route
            path={CATEGORIES_ROUTES.LIST}
            element={
              <Suspense>
                <CategoryListModule />
              </Suspense>
            }
          />
          <Route
            path={CATEGORIES_ROUTES.EDIT}
            element={<Navigate to={CATEGORIES_ROUTES.NEW} replace />}
          />
          <Route
            path={`${CATEGORIES_ROUTES.EDIT}/:categoryId`}
            element={<CategoryFormModule />}
          >
            <Route
              index
              element={
                <Suspense>
                  <CategoryViewModule />
                </Suspense>
              }
            />
            <Route
              path={CATEGORIES_ROUTES.ADDPRODUCTS}
              element={
                <Suspense>
                  <CategoryProductsViewModule />
                </Suspense>
              }
            ></Route>
          </Route>
        </Route>
        <Route
          path={PROFILE_ROUTES.ROOT}
          element={
            <Suspense>
              <Profile />
            </Suspense>
          }
        >
          <Route
            index
            element={
              <Suspense>
                <ProfileHome />
              </Suspense>
            }
          />
          <Route
            path={PROFILE_ROUTES.BUSINESS_SECTORS}
            element={
              <Suspense>
                <BusinessSectors />
              </Suspense>
            }
          />
          <Route
            path={PROFILE_ROUTES.STORE_HOURS}
            element={
              <Suspense>
                <StoreHours />
              </Suspense>
            }
          />
        </Route>
        <Route
          path={`${ORDERS_ROUTES.ROOT}/${ORDERS_ROUTES.CREATE}`}
          element={
            <Suspense>
              <OrderCreate />
            </Suspense>
          }
        />
        <Route
          path={ORDERS_ROUTES.ROOT}
          element={
            <Suspense>
              <Orders />
            </Suspense>
          }
        >
          <Route
            path={`${ORDERS_ROUTES.DETAIL}`}
            element={
              <Suspense>
                <OrderDetail />
              </Suspense>
            }
          />
        </Route>
        <Route path="abandoned-carts" element={<CartsList />} />
        <Route path="seo">
          <Route
            index
            element={
              <Suspense>
                <SeoOverview />
              </Suspense>
            }
          />
          <Route
            path=":productId"
            element={
              <Suspense>
                <SeoProductDetail />
              </Suspense>
            }
          />
        </Route>
        <Route path="google-analytics">
          <Route
            index
            element={
              <Suspense>
                <GaConfig />
              </Suspense>
            }
          />
          <Route
            path="find-id"
            element={
              <Suspense>
                <GaFindId />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="catalog-sorter"
          element={
            <Suspense>
              <CatalogSorterPage />
            </Suspense>
          }
        />
        <Route path="facebook-pixel">
          <Route
            index
            element={
              <Suspense>
                <FbPixelHome />
              </Suspense>
            }
          />
          <Route
            path="connect"
            element={
              <Suspense>
                <FbPixelConnect />
              </Suspense>
            }
          />
          <Route
            path="help"
            element={
              <Suspense>
                <FbPixelHelp />
              </Suspense>
            }
          />
        </Route>
        <Route path="personalization" element={<Personalization />} />
        <Route
          path="checkout"
          element={
            <Suspense>
              <Checkout />
            </Suspense>
          }
        />
        <Route
          path="checkout/status/:orderId"
          element={
            <Suspense>
              <Status />
            </Suspense>
          }
        />
        <Route
          path="plans"
          element={
            <Suspense>
              <Plans />
            </Suspense>
          }
        />
        <Route
          path="contact-button"
          element={
            <Suspense>
              <ContactButtonAddon />
            </Suspense>
          }
        />
        <Route path={CUSTOMERS_ROUTES.ROOT}>
          <Route
            index
            element={
              <Suspense>
                <CustomersList />
              </Suspense>
            }
          />
          <Route
            path={CUSTOMERS_ROUTES.NEW}
            element={
              <Suspense>
                <CustomerDetail />
              </Suspense>
            }
          />
          <Route
            path={CUSTOMERS_ROUTES.GROUP}
            element={
              <Suspense>
                <CustomersGroup />
              </Suspense>
            }
          />
          <Route
            path={CUSTOMERS_ROUTES.DETAIL}
            element={
              <Suspense>
                <CustomerDetail />
              </Suspense>
            }
          />
        </Route>
        <Route path="voucher">
          <Route
            index
            element={
              <Suspense>
                <VoucherList />
              </Suspense>
            }
          />
          <Route
            path="create"
            element={
              <Suspense>
                <VoucherCreation />
              </Suspense>
            }
          />
        </Route>
        <Route path="offers">
          <Route
            index
            element={
              <Suspense>
                <OffersList />
              </Suspense>
            }
          />
          <Route
            path="create"
            element={
              <Suspense>
                <OfferWizard />
              </Suspense>
            }
          />
          <Route
            path=":id/edit"
            element={
              <Suspense>
                <OfferWizard isEditMode />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense>
                <OfferDetail />
              </Suspense>
            }
          />
        </Route>
        <Route path="coupons">
          <Route
            index
            element={
              <Suspense>
                <CouponsList />
              </Suspense>
            }
          />
          <Route
            path="create"
            element={
              <Suspense>
                <CouponWizard />
              </Suspense>
            }
          />
          <Route
            path=":id/edit"
            element={
              <Suspense>
                <CouponWizard isEditMode />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense>
                <CouponDetail />
              </Suspense>
            }
          />
        </Route>
        <Route path="kits">
          <Route
            index
            element={
              <Suspense>
                <KitsList />
              </Suspense>
            }
          />
          <Route
            path="select"
            element={
              <Suspense>
                <KitProductSelector />
              </Suspense>
            }
          />
          <Route
            path="create"
            element={
              <Suspense>
                <KitForm />
              </Suspense>
            }
          />
          <Route
            path=":kitId/edit"
            element={
              <Suspense>
                <KitForm />
              </Suspense>
            }
          />
        </Route>
        <Route path="meli">
          <Route
            index
            element={
              <Suspense>
                <MeliModule />
              </Suspense>
            }
          />
        </Route>
        <Route
          path={GOOGLE_SHOPPING_ROUTES.ROOT_GENERAL}
          element={
            <Suspense>
              <GoogleShoppingModule />
            </Suspense>
          }
        >
          <Route
            path={GOOGLE_SHOPPING_ROUTES.ROOT_GSH_CONNECTED}
            element={
              <Suspense>
                <GoogleShoppingConfigurationModule />
              </Suspense>
            }
          />
          <Route
            path={GOOGLE_SHOPPING_ROUTES.ROOT_INFO}
            element={
              <Suspense>
                <GoogleShoppingInfoModule />
              </Suspense>
            }
          />
        </Route>
        <Route path={DELIVERY_ROUTES.ROOT} element={<DeliveryLayout />}>
          <Route
            index
            element={
              <Suspense>
                <DeliveryHome />
              </Suspense>
            }
          />
          <Route
            path={DELIVERY_ROUTES.SETTINGS}
            element={
              <Suspense>
                <DeliverySettings />
              </Suspense>
            }
          />
          <Route
            path={DELIVERY_ROUTES.COVERAGE}
            element={
              <Suspense>
                <DeliveryCoverage />
              </Suspense>
            }
          />
          <Route
            path={DELIVERY_ROUTES.CREATE}
            element={
              <Suspense>
                <DeliveryCreate />
              </Suspense>
            }
          />
          <Route
            path={DELIVERY_ROUTES.CREATE_WITH_ORDER}
            element={
              <Suspense>
                <DeliveryCreate />
              </Suspense>
            }
          />
          <Route
            path={DELIVERY_ROUTES.MANUAL_CHECKOUT}
            element={
              <Suspense>
                <DeliveryCheckout />
              </Suspense>
            }
          />
          <Route
            path={DELIVERY_ROUTES.CHECKOUT}
            element={
              <Suspense>
                <DeliveryCheckout />
              </Suspense>
            }
          />
          <Route
            path={DELIVERY_ROUTES.DETAIL}
            element={
              <Suspense>
                <DeliveryDetail />
              </Suspense>
            }
          />
        </Route>
        <Route path="configuration" element={<ConfigurationLayout />}>
          <Route
            index
            element={
              <Suspense>
                <Configuration />
              </Suspense>
            }
          />
          <Route path="domain">
            <Route
              index
              element={
                <Suspense>
                  <Domain />
                </Suspense>
              }
            />
            <Route
              path="search"
              element={
                <Suspense>
                  <SearchDomain />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="terms-and-conditions"
            element={
              <Suspense>
                <TermsAndConditionsPage />
              </Suspense>
            }
          />
          <Route path="pasarelas" element={<PaymentGatewayLayout />}>
            <Route
              index
              element={
                <Suspense>
                  <PaymentGatewayModule />
                </Suspense>
              }
            />
            <Route
              path="manual/nuevo"
              element={
                <Suspense>
                  <ManualPaymentModule />
                </Suspense>
              }
            />
            <Route
              path="manual/default/:defaultIndex"
              element={
                <Suspense>
                  <ManualPaymentModule />
                </Suspense>
              }
            />
            <Route
              path="manual/:index"
              element={
                <Suspense>
                  <ManualPaymentModule />
                </Suspense>
              }
            />
            <Route
              path=":providerPath"
              element={
                <Suspense>
                  <PaymentGatewayDetailModule />
                </Suspense>
              }
            />
          </Route>
        </Route>
        <Route path="mobile" element={<Mobile />} />
        <Route
          path={MARKETPLACE_ROUTES.ROOT}
          element={
            <Suspense>
              <MarketplaceModule />
            </Suspense>
          }
        >
          <Route
            index
            element={
              <Suspense>
                <MarketplaceView />
              </Suspense>
            }
          />
          <Route
            path={MARKETPLACE_ROUTES.USE}
            element={
              <Suspense>
                <AddonWebviewView />
              </Suspense>
            }
          />
          <Route
            path={MARKETPLACE_ROUTES.PARTNER}
            element={
              <Suspense>
                <PartnershipDetailView />
              </Suspense>
            }
          />
          <Route
            path={MARKETPLACE_ROUTES.DETAIL}
            element={
              <Suspense>
                <AddonDetailView />
              </Suspense>
            }
          />
        </Route>
      </Route>
      <Route path="/health" element={<Health />} />
    </Routes>
  );
}

export default App;
